import React from 'react';
import { motion, Variants } from 'framer-motion';

interface StaggerChildrenProps {
  children: React.ReactNode;
  /** Delay between each child animation (in seconds) */
  staggerDelay?: number;
  /** Initial delay before first child animates (in seconds) */
  initialDelay?: number;
  /** Distance to slide from (in pixels) */
  distance?: number;
  /** Animation duration for each child (in seconds) */
  duration?: number;
  /** Custom className for container */
  className?: string;
  /** Custom style for container */
  style?: React.CSSProperties;
}

/**
 * StaggerChildren - Container for staggered child animations
 *
 * Wraps children and animates them sequentially with a staggered delay.
 * Each child gets fade + slide up animation automatically.
 * Respects prefers-reduced-motion accessibility preference.
 *
 * @example
 * ```tsx
 * <StaggerChildren staggerDelay={0.1}>
 *   {items.map(item => (
 *     <Card key={item.id}>{item.title}</Card>
 *   ))}
 * </StaggerChildren>
 * ```
 */
export const StaggerChildren: React.FC<StaggerChildrenProps> = ({
  children,
  staggerDelay = 0.05,
  initialDelay = 0,
  distance = 20,
  duration = 0.4,
  className,
  style,
}) => {
  const containerVariants: Variants = {
    hidden: {
      opacity: 0,
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: staggerDelay,
        delayChildren: initialDelay,
      },
    },
  };

  const itemVariants: Variants = {
    hidden: {
      opacity: 0,
      y: distance,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration,
        ease: [0.4, 0.0, 0.2, 1], // Material Design easing
      },
    },
  };

  // Wrap each child in a motion.div
  const wrappedChildren = React.Children.map(children, (child, index) => {
    if (!React.isValidElement(child)) {
      return child;
    }

    return (
      <motion.div
        key={index}
        variants={itemVariants}
        style={{ willChange: 'transform, opacity' }}
      >
        {child}
      </motion.div>
    );
  });

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className={className}
      style={style}
    >
      {wrappedChildren}
    </motion.div>
  );
};
